import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId } from "../../utils/currentSalon";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { listSalonUsers } from "../../api/salonUsers";
import { listClientSurveys } from "../../api/clientSurveys";
import { updateFeedbackTags } from "../../api/marketingFeedback";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import ModalCloseX from "../../components/common/ModalCloseX";
import SBINModal from "../../components/modals/SBINModal";
import "../MarketingSectionCompact.css";

const HELP_SECTIONS = [
  {
    heading: "How client surveys work",
    bullets: [
      "Surveys are emailed automatically when tickets close (if enabled).",
      "Ratings below 4 create an internal follow-up for managers.",
      "Positive feedback can trigger a public review ask (if enabled).",
      "Use Resend from a survey detail to reissue a link if needed.",
    ],
  },
];

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
  { value: "expired", label: "Expired" },
];

const RATING_OPTIONS = [
  { value: "", label: "All ratings" },
  { value: "5", label: "5" },
  { value: "4", label: "4" },
  { value: "3", label: "3" },
  { value: "2", label: "2" },
  { value: "1", label: "1" },
];

const TAG_OPTIONS = [
  "wait_time",
  "cleanliness",
  "pain",
  "staff_attitude",
  "pricing",
  "booking_issue",
  "quality_issue",
];

const SENTIMENT_OPTIONS = [
  { value: "", label: "Select sentiment" },
  { value: "positive", label: "Positive" },
  { value: "neutral", label: "Neutral" },
  { value: "negative", label: "Negative" },
];

const DEFAULT_FILTERS = {
  date_start: "",
  date_end: "",
  overall_rating: "",
  staff_user_id: "",
  status: "",
  needs_followup: false,
};

const promptLabel = (status) => {
  if (!status) return "-";
  if (status === "clicked") return "Clicked";
  if (status === "prompted") return "Prompted";
  if (status === "scheduled") return "Scheduled";
  return "Pending";
};

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const csvEscape = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, "\"\"")}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function ClientSurveys() {
  const navigate = useNavigate();
  const { user, token, currentSalon } = useAuth();
  const salonId = getCurrentSalonId(user);
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [salonUsers, setSalonUsers] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({
    theme_tags: [],
    sentiment: "",
    needs_review: false,
  });
  const [savingTags, setSavingTags] = useState(false);

  const loadSurveys = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const data = await listClientSurveys(
        {
          salon_id: salonId,
          date_start: filters.date_start || undefined,
          date_end: filters.date_end || undefined,
          overall_rating: filters.overall_rating || undefined,
          staff_user_id: filters.staff_user_id || undefined,
          status: filters.status || undefined,
          needs_followup: filters.needs_followup ? true : undefined,
        },
        token
      );
      setSurveys(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load client surveys.");
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  }, [filters, salonId, token]);

  useEffect(() => {
    loadSurveys();
  }, [loadSurveys]);

  useEffect(() => {
    if (!token) return;
    listSalonUsers(token)
      .then((data) => {
        setSalonUsers(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setSalonUsers([]);
      });
  }, [token]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const openEdit = (item) => {
    if (!item?.review_id) return;
    setEditItem(item);
    setEditForm({
      theme_tags: Array.isArray(item?.theme_tags) ? item.theme_tags : [],
      sentiment: item?.sentiment || "",
      needs_review: Boolean(item?.needs_review),
    });
  };

  const toggleTag = (tag) => {
    setEditForm((prev) => {
      const exists = prev.theme_tags.includes(tag);
      return {
        ...prev,
        theme_tags: exists
          ? prev.theme_tags.filter((entry) => entry !== tag)
          : [...prev.theme_tags, tag],
      };
    });
  };

  const handleSaveTags = async () => {
    if (!editItem?.review_id) return;
    setSavingTags(true);
    setError("");
    try {
      await updateFeedbackTags(
        editItem.review_id,
        {
          theme_tags: editForm.theme_tags,
          sentiment: editForm.sentiment || null,
          needs_review: Boolean(editForm.needs_review),
        },
        token
      );
      setEditItem(null);
      await loadSurveys();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save feedback tags.");
    } finally {
      setSavingTags(false);
    }
  };

  const handleExport = () => {
    const headers = [
      "Date",
      "Client",
      "Ticket #",
      "Staff",
      "Overall",
      "Criteria Avg",
      "Comment",
      "Status",
      "Follow-up",
      "Public Review",
      "Tags",
      "Sentiment",
      "Needs Review",
      "Channel",
    ];
    const rows = surveys.map((row) => [
      row.created_at_utc
        ? formatDateTimeInTimeZone(row.created_at_utc, salonTimezone)
        : "",
      row.client_name || "",
      row.ticket_number || row.ticket_id || "",
      row.staff_name || "",
      row.overall_rating ?? "",
      Number.isFinite(row.criteria_avg) ? row.criteria_avg.toFixed(2) : "",
      row.comment || (row.comment_present ? "Yes" : ""),
      row.status || "",
      row.followup_status || "",
      promptLabel(row.public_review_prompt_status),
      Array.isArray(row.theme_tags) ? row.theme_tags.join("; ") : "",
      row.sentiment || "",
      row.needs_review ? "Yes" : "No",
      row.booking_channel || "",
    ]);
    downloadCsv("client-surveys-feedback.csv", headers, rows);
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/manager/schedule");
  };

  return (
    <div className="marketingSectionShell">
      <div className="marketingSectionWide marketing-section-wide marketing-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 pr-20 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">
              Client Surveys
            </h1>
            <button
              type="button"
              onClick={handleExport}
              className="rounded-full bg-white/15 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-white/25"
            >
              Export CSV
            </button>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Client Survey Inbox"
              subtitle="Track submitted surveys, follow-up needs, and resend links."
              sections={HELP_SECTIONS}
              storageKey="sbin_client_surveys_help"
            />

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Filters
                  </div>
                  <div className="text-sm text-slate-500">
                    Filter by date, status, rating, or staff.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={loadSurveys}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Apply filters
                  </button>
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Reset
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-4">
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date start
                  </label>
                  <input
                    type="date"
                    value={filters.date_start}
                    onChange={(e) => handleFilterChange("date_start", e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-300 px-3"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Date end
                  </label>
                  <input
                    type="date"
                    value={filters.date_end}
                    onChange={(e) => handleFilterChange("date_end", e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-300 px-3"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Overall rating
                  </label>
                  <select
                    value={filters.overall_rating}
                    onChange={(e) =>
                      handleFilterChange("overall_rating", e.target.value)
                    }
                    className="h-11 w-full rounded-xl border border-slate-300 px-3 bg-white"
                  >
                    {RATING_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-300 px-3 bg-white"
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Staff/technician
                  </label>
                  <select
                    value={filters.staff_user_id}
                    onChange={(e) =>
                      handleFilterChange("staff_user_id", e.target.value)
                    }
                    className="h-11 w-full rounded-xl border border-slate-300 px-3 bg-white"
                  >
                    <option value="">All staff</option>
                    {salonUsers.map((userItem) => (
                      <option key={userItem.id} value={userItem.id}>
                        {userItem.full_name || userItem.username || userItem.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Needs follow-up
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={filters.needs_followup}
                      onChange={(e) =>
                        handleFilterChange("needs_followup", e.target.checked)
                      }
                      className="h-4 w-4 rounded border-slate-300 text-slate-900"
                    />
                    Show only follow-ups
                  </label>
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white">
              <div className="px-4 py-3 border-b border-slate-200">
                <div className="text-base md:text-lg font-semibold text-slate-900">
                  Survey activity
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Client</th>
                      <th className="px-4 py-3 text-left">Ticket #</th>
                      <th className="px-4 py-3 text-left">Staff</th>
                      <th className="px-4 py-3 text-left">Overall</th>
                      <th className="px-4 py-3 text-left">Criteria avg</th>
                      <th className="px-4 py-3 text-left">Comment</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Follow-up</th>
                      <th className="px-4 py-3 text-left">Public review</th>
                      <th className="px-4 py-3 text-left">Tags</th>
                      <th className="px-4 py-3 text-left">Sentiment</th>
                      <th className="px-4 py-3 text-left">Needs review</th>
                      <th className="px-4 py-3 text-left">Channel</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={15} className="px-4 py-6 text-center text-slate-500">
                          Loading surveys...
                        </td>
                      </tr>
                    )}
                    {!loading && surveys.length === 0 && (
                      <tr>
                        <td colSpan={15} className="px-4 py-6 text-center text-slate-500">
                          No survey results found.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      surveys.map((row) => (
                        <tr
                          key={row.id}
                          className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/manager/marketing/client-surveys/${row.id}`)}
                        >
                          <td className="px-4 py-3 text-slate-700">
                            {row.created_at_utc
                              ? formatDateTimeInTimeZone(row.created_at_utc, salonTimezone)
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.client_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.ticket_number || row.ticket_id || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.staff_name || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.overall_rating || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {Number.isFinite(row.criteria_avg)
                              ? row.criteria_avg.toFixed(2)
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.comment || (row.comment_present ? "Yes" : "-")}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                              {row.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.followup_status ? row.followup_status : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {promptLabel(row.public_review_prompt_status)}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {Array.isArray(row.theme_tags) && row.theme_tags.length
                              ? row.theme_tags.map(titleCase).join(", ")
                              : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.sentiment ? titleCase(row.sentiment) : "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.needs_review ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-3 text-slate-700">
                            {row.booking_channel ? titleCase(row.booking_channel) : "-"}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              disabled={!row.review_id}
                              onClick={(event) => {
                                event.stopPropagation();
                                openEdit(row);
                              }}
                              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SBINModal
        isOpen={Boolean(editItem)}
        onClose={() => setEditItem(null)}
        title="Update Feedback Tags"
        size="lg"
        fitContent
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditItem(null)}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveTags}
              disabled={savingTags}
              className="rounded-full bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-60"
            >
              {savingTags ? "Saving..." : "Save"}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          {editItem && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              <div className="font-semibold text-slate-900">
                {editItem.client_name || "Client"}
              </div>
              <div>{editItem.ticket_number || editItem.ticket_id || "Ticket"}</div>
              {editItem.comment && <div className="mt-2">{editItem.comment}</div>}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase text-slate-500">
              Theme tags
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {TAG_OPTIONS.map((tag) => {
                const selected = editForm.theme_tags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold border ${
                      selected
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-300 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    {titleCase(tag)}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs font-semibold uppercase text-slate-500">
                Sentiment
              </label>
              <select
                value={editForm.sentiment}
                onChange={(event) =>
                  setEditForm((prev) => ({ ...prev, sentiment: event.target.value }))
                }
                className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              >
                {SENTIMENT_OPTIONS.map((option) => (
                  <option key={option.value || "none"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={editForm.needs_review}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    needs_review: event.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-slate-300 text-slate-900"
              />
              Needs review
            </label>
          </div>
        </div>
      </SBINModal>
    </div>
  );
}
